
import {catchError, map, switchMap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import {throwError as observableThrowError, Observable} from 'rxjs';

import {Helpers} from '../../common/helpers/helpers';
import {JsonBaseBuilder} from '../../common/builder/json-base-builder';
import {TreeViewNodeAce} from './tree-view-node-ace.model';
import {ConfigService} from '../../core/config.service';
import {TreeView} from './tree-view/model/tree-view-default.model';
import {Gerarchia} from '../gerarchia/gerarchia.model';
import {EntitaOrganizzativa} from '../entitaorganizzativa/entitaorganizzativa.model';
import {TipoGerarchia} from '../../+configurazione/tipogerarchia/tipogerarchia.model';
import {CommonService} from '../../common/controller/common.service';
import {ApiMessageService, MessageType} from '../../core/api-message.service';
import {Router} from '@angular/router';
import {MODULE_ANAGRAFICA} from '../../app-routing.module';

import {SpringError} from '../../common/model/spring-error.model';

@Injectable()
export class GerarchiaTreeService extends CommonService<TreeView> {

  public static INTERNAL_ROUTE = 'gerarchia-tree';

  public static ROUTE = 'gerarchia/tree';


  public constructor(protected httpClient: HttpClient,
                     protected apiMessageService: ApiMessageService,
                     protected router: Router,
                     protected configService: ConfigService) {
    super(httpClient, apiMessageService, router, configService);
  }

  public buildInstance(json: any): TreeView {
    throw new Error('Not implemented');
  }

  public getModule(): string {
    return MODULE_ANAGRAFICA;
  }

  public getApiService(): string {
    return ConfigService.ACE;
  }

  public getRoute(): string {
    return GerarchiaTreeService.ROUTE;
  }

  /**
   * Trasformazione da dto a gerarchia-tree utilizzata nel tree-view.
   * @param item
   * @param level
   * @param focusedId
   * @returns {TreeViewNode}
   */
  public buildForEntitaorganizzativa(item, level, focusedId, tipoGerarchiaId, dataRif): TreeViewNodeAce {

    const node = new TreeViewNodeAce();

    node.gerarchiaLabel = item.gerarchiaLabel;
    node.tipoGerarchiaId = tipoGerarchiaId;
    node.dataRif = dataRif;
    node.gerarchiaFrom = item.from ? new Date(item.from) : null;
    node.gerarchiaTo = item.to ? new Date(item.to) : null;
    node.gerarchiaId = item.gerarchiaId;
    node.figlioGuida = item.figlioGuida;

    node.structError = item.structError;
    node.dateError = item.dateError;
    node.eoPathBottom = item.eoPathBottom;
    node.eoPathTop = item.eoPathTop;
    node.eoNode = item.eoNode;

    node.delegabile = item.delegabile;

    node.entity = JsonBaseBuilder.jsonToEntitaOrganizzativa(item.entitaOrganizzativa);

    node.setId(item.entitaOrganizzativa.id);

    node.setChildren(item.children.map( child =>
      this.buildForEntitaorganizzativa(child, level + 1, focusedId, tipoGerarchiaId, dataRif)));

    node.setFiltered(true);

    node.getChildren().forEach( child => {
      if (child.isExpanded()) {
        node.setExpanded(true);
      }
    })

    if (focusedId === node.getId()) {
      node.setExpanded(true);
    }

    return node;
  }

  /**
   * La gerarchia-tree delle entità organizzative. Più che un albero è una foresta!.
   * @param tipoGerarchiaId
   * @param force
   * @param id
   * @returns {Observable<R|T>}
   */
  public getTree(tipoGerarchiaId: number, rootNode: Gerarchia,
                 dataRif: Date, force: Boolean = false, selectedId, filter): Observable<TreeView> {

    let path = this.getRequestMapping();
    if (rootNode) {
      path = path + '/' + tipoGerarchiaId + '/' + rootNode.getId();
    }

    let params = new HttpParams();
    if (tipoGerarchiaId) {
      params = params.set('tipo', tipoGerarchiaId + '');
    }

    if (dataRif) {
      params = params.set('dateRif', Helpers.formatDate(dataRif));
    }

    return this.configService.getApiBase().pipe(switchMap( apiBase => {

      return this.httpClient.get<Node[]>(apiBase + path, { params: params }).pipe(map( (rootsDto) => {

        const tree = new TreeView();
        tree.withNodes(rootsDto.map(root => this.buildForEntitaorganizzativa(root, 0, selectedId, tipoGerarchiaId, dataRif)));

        if (filter) {
          tree.filterTree(filter.target.value);
        }
        return tree;
      }));
    }));
  }

  /**
   * La gerarchia-tree delle entità organizzative. Più che un albero è una foresta!.
   * @param tipoGerarchiaId
   * @param force
   * @param id
   * @returns {Observable<R|T>}
   */
  public treeByEntitaOrganizzativa(entitaOrganizzativa: EntitaOrganizzativa,
                                   dataRif: Date, tipoGerarchia: TipoGerarchia): Observable<TreeView> {
    let params = new HttpParams();
    if (tipoGerarchia) {
      params = new HttpParams().set('dateRif', Helpers.formatDate(dataRif)).set('tipo', tipoGerarchia.getId() + '');
    } else {
      params = new HttpParams().set('dateRif', Helpers.formatDate(dataRif));
    }

    return this.configService.getApiBase().pipe(switchMap( apiBase => {
      const path = '/entitaorganizzativa/' + entitaOrganizzativa.getId();
      return this.httpClient.get<Node[]>(apiBase + this.getRequestMapping() + path, { params: params }).pipe(map( (rootsDto) => {
        const tree = new TreeView();
        if (rootsDto.length === 0) {
          return tree;
        }
        tree.withNodes(rootsDto.map(root => this.buildForEntitaorganizzativa(root, 0, null, null, dataRif)));
        return tree;
      }));
    }));
  }

  /**
   * Le roots vengono splittate in attrettanti trees.
   * @param tipoGerarchiaId
   * @param force
   * @param id
   * @returns {Observable<R|T>}
   */
  public treesByEntitaOrganizzativa(entitaOrganizzativa: EntitaOrganizzativa,
                                   dataRif: Date, tipoGerarchia: TipoGerarchia): Observable<TreeView[]> {
    let params = new HttpParams();
    if (tipoGerarchia) {
      params = new HttpParams().set('dateRif', Helpers.formatDate(dataRif)).set('tipo', tipoGerarchia.getId() + '');
    } else {
      params = new HttpParams().set('dateRif', Helpers.formatDate(dataRif));
    }

    return this.configService.getApiBase().pipe(switchMap( apiBase => {
      const path = '/entitaorganizzativa/' + entitaOrganizzativa.getId();
      return this.httpClient.get<Node[]>(apiBase + this.getRequestMapping() + path, { params: params }).pipe(map( (rootsDto) => {
        let trees = [];
        if (rootsDto.length === 0) {
          return trees;
        }
        trees = rootsDto.map( root => {
          const tree = new TreeView();
          tree.withNodes([this.buildForEntitaorganizzativa(root, 0, null, null, dataRif)]);
          tree.onExpandTree();
          return tree;
        });
        return trees;

      }));
    }));
  }

  public loadCsv(dataRif: Date, tipoGerarchia: TipoGerarchia, term: string): Observable<any> {
    let params =  new HttpParams().set('data', dataRif ? Helpers.formatDate(dataRif) : '')
      .set('tipo', tipoGerarchia.getId() + '')
      .set('term', term);
    return this.configService.getApiBase()
    .pipe(
      switchMap((apiBase) => {
        return this.httpClient.get(apiBase + this.getApiService() + '/v' + this.getApiVersion() + '/' + 'gerarchia/csv', {params: params, responseType: 'blob'})
          .pipe(
            map((result) => {
              return result;
            }),
            catchError( (error: HttpErrorResponse) => {
              const springError = new SpringError(error);
              this.apiMessageService.sendMessage(MessageType.ERROR,  springError.getRestErrorMessage());
              return observableThrowError(error.error);
            })
          );
      })
    );
  }

  /**
   * La gerarchia-tree su cui l'utente può delegare il ruolo.
   * @param tipoGerarchiaId
   * @param force
   * @param id
   * @returns {Observable<R|T>}
   */
  public gerarchieTargetDelega(utenteId: number, ruoloId: number): Observable<TreeView> {

    const params = new HttpParams().set('utenteId', utenteId + '').set('ruoloId', ruoloId + '');

    return this.configService.getApiBase().pipe(switchMap( apiBase => {

      const path = '/ace-webapp/v1/delega/gerarchietarget';

      return this.httpClient.get<Node[]>(apiBase + path, { params: params }).pipe(map( (rootsDto) => {

        const tree = new TreeView();
        tree.withNodes(rootsDto.map(root => this.buildForEntitaorganizzativa(root, 0, null,
          null, new Date())));
        return tree;
      }));
    }));
  }

}
